import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Check,
  Layers,
  Wallet,
  Calculator,
  Building2,
  Store,
  Stethoscope,
  Network,
  ShieldCheck,
  Globe2,
} from "lucide-react";
import { FaFacebookF, FaInstagram, FaGoogle } from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import PricingAddons from "./PricingAddons";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import SEO from "../SEO";
import { FaqSection } from "../FaqList";
import { HOW_AGENCIES_EARN_FAQS } from "../../data/pageFaqs";
import { Reveal, Overline } from "../Reveal";
import AnimatedNumber from "../AnimatedNumber";
import CurrencySelector from "../CurrencySelector";
import { SIGNUP_URL } from "../../data/content";
import { CurrencyProvider, useCurrency } from "../../contexts/CurrencyContext";
import { convertFromINR, formatCurrencyCompact } from "../../data/currencies";

// Formats a fixed INR-denominated figure (tier prices, service-table rows,
// setup-fee examples, plan pricing) in the visitor's selected currency -
// same convertFromINR conversion the rest of the pricing pages use.
function useMoney() {
  const { currency } = useCurrency();
  return (inrAmount) => `${currency.symbol}${convertFromINR(inrAmount, currency).toLocaleString("en-US")}`;
}

// ---------------------------------------------------------------------------
// Section data - kept local to this page since it's a distinct, illustrative
// resale-pricing narrative rather than the platform's actual plan data.
// ---------------------------------------------------------------------------

const STEPS = [
  {
    title: "Get Your White-Label Platform",
    desc: "Your branding, logo, domain and client-facing platform.",
  },
  {
    title: "Choose What You Sell",
    desc: "Choose from AI Automation, QR Software and Instant Websites.",
  },
  {
    title: "Create Your Packages",
    desc: "Combine multiple products into your own Tier 1, Tier 2 and Tier 3 packages.",
  },
  {
    title: "Sell to Local Businesses",
    desc: "Target restaurants, salons, clinics, real estate companies, retailers, hotels, professionals, gyms, schools and other local businesses.",
  },
  {
    title: "Build Recurring Revenue",
    desc: "Charge clients monthly or yearly for software, automation, marketing and ongoing services.",
  },
];

const CATEGORIES = [
  {
    title: "AI Automation",
    icon: Sparkles,
    color: "#FF6B00",
    tint: "rgba(255,107,0,0.10)",
    items: [
      "Google Business Profile Automation",
      "Facebook Automation",
      "Instagram Automation",
      "WhatsApp API CRM",
      "WordPress Automation",
      "AEO + GEO Automation",
      "AI Content Generation",
      "AI Review Replies",
      "Social Media Automation",
      "Review & Reputation Automation",
      "AI Workflow Automation",
    ],
  },
  {
    title: "QR-Based Digital Software",
    icon: Layers,
    color: "#2563EB",
    tint: "rgba(37,99,235,0.10)",
    items: ["MagicQR + Review & Feedback", "Digital Menu", "Digital Visiting Card", "Bio Links", "Appointment Scheduling"],
  },
  {
    title: "Instant Website",
    icon: Building2,
    color: "#0D9488",
    tint: "rgba(13,148,136,0.10)",
    items: ["Instant Website"],
  },
];

// Building-block pricing an agency combines into its own client packages -
// each priced across three tiers instead of fixed named bundles.
// Prices are ordered Tier 1 -> Tier 3 to match city-tier economics: Tier 1
// (metro/high-paying markets) commands the highest price, Tier 3 the lowest.
const ACCOUNT_CREATION = {
  title: "Account Creation",
  subtitle: "One-time setup of Facebook, Instagram & Google Business Profile accounts.",
  unit: "one-time",
  prices: [5000, 2500, 1000],
  channels: ["facebook", "instagram", "google"],
};

const SINGLE_CHANNEL = {
  title: "Single Channel Automation",
  subtitle: "Automate one channel of the client's choice - Facebook, Instagram or Google Business Profile.",
  unit: "/ month per channel",
  prices: [15000, 10000, 5000],
  channels: ["facebook", "instagram", "google"],
};

const ALL_CHANNELS_COMBINED = {
  title: "All Channels Combined",
  subtitle: "Facebook + Instagram + Google Business Profile automation, bundled together.",
  unit: "/ month",
  prices: [30000, 20000, 15000],
  channels: ["facebook", "instagram", "google"],
};

const PACKAGE_CATEGORIES = [ACCOUNT_CREATION, SINGLE_CHANNEL, ALL_CHANNELS_COMBINED];

const CHANNEL_ICONS = {
  facebook: { Icon: FaFacebookF, bg: "#1877F2", color: "#fff" },
  instagram: { Icon: FaInstagram, bg: "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)", color: "#fff" },
  google: { Icon: FaGoogle, bg: "#fff", color: "#4285F4", border: true },
};

const QR_SUITE_PRODUCTS = ["MagicQR + Review & Feedback", "Digital Visiting Card", "Digital Menu", "Bio Links"];
const QR_SUITE_PRICES = [6000, 3000, 1000];

// Reuses the previously agreed per-product resale prices - same numbers,
// now sold individually instead of buried in a generic services table.
const BUSINESS_SOFTWARE = [
  { icon: Globe2, product: "Instant Website", monthly: 4999, yearly: 49999 },
];

// Summary cards for the two multi-product suites, shown alongside the
// channel packages above - full per-product pricing lives further down
// the page (QrSuiteSection / BusinessSoftwareSection).
const PRODUCT_SUITE_CARDS = [
  {
    key: "qr-suite",
    icon: Layers,
    title: "QR Software",
    subtitle: "MagicQR, Visiting Card, Digital Menu & Bio Links - sell individually or bundle.",
    prices: QR_SUITE_PRICES,
    unit: "/ year per product",
    anchor: "#qr-suite",
  },
  {
    key: "business-software",
    icon: Building2,
    title: "Instant Website",
    subtitle: "A one-page website for every client - stack it onto any plan.",
    prices: [5999, 3999, 2999],
    unit: "/ month per product",
    anchor: "#business-software",
  },
];

// "All Channels Combined" doubles as the flagship recurring package used for
// the calculator's tier presets.
const PACKAGE_TIERS = [
  { key: "tier1", label: "TIER 1", monthly: ALL_CHANNELS_COMBINED.prices[0] },
  { key: "tier2", label: "TIER 2", monthly: ALL_CHANNELS_COMBINED.prices[1] },
  { key: "tier3", label: "TIER 3", monthly: ALL_CHANNELS_COMBINED.prices[2] },
];

const CALCULATOR_PRESETS = [10, 25, 50, 100];

const BUNDLES = [
  {
    icon: Store,
    name: "Restaurant Package",
    items: ["Digital Menu", "MagicQR", "Google Business Profile Automation", "Review Management", "Website"],
    price: "₹7,999 – ₹14,999/month",
  },
  {
    icon: Stethoscope,
    name: "Clinic Package",
    items: ["Google Business Profile", "Review Automation", "Website", "WhatsApp API CRM", "AI Content", "Lead Management"],
    price: "₹9,999 – ₹19,999/month",
  },
  {
    icon: Building2,
    name: "Real Estate Package",
    items: ["Google Business Profile", "Social Automation", "WhatsApp API CRM", "Lead Management", "AI Content", "Website", "AEO/GEO"],
    price: "₹14,999 – ₹29,999/month",
  },
  {
    icon: Network,
    name: "Multi-Location Business",
    items: ["GBP Automation", "Social Automation", "AEO/GEO", "WhatsApp API CRM", "Review Management", "AI Comments Reply", "Reporting"],
    price: "₹25,000 – ₹50,000+/month",
  },
];

const OWNERSHIP = ["Your Brand", "Your Domain", "Your Client Relationship", "Your Pricing", "Your Packages", "Your Recurring Revenue"];

const REVENUE_STREAMS = [
  "Monthly Software Subscription",
  "Annual Software Subscription",
  "One-Time Setup Fees",
  "Digital Marketing Services",
  "SEO Services",
  "Social Media Management",
  "Website Setup",
  "WhatsApp CRM Setup",
  "Automation Setup",
  "Lead Generation Services",
  "Review & Reputation Management",
  "Consulting & Strategy",
  "Premium Support / Account Management",
];

const INDUSTRY_PHOTOS = [
  { src: "/gym.webp", label: "Gym & Fitness Centres" },
  { src: "/restaurent-%20bar.webp", label: "Restaurants & Bars" },
  { src: "/salon%20sap.webp", label: "Salon Owners" },
  { src: "/realstate.webp", label: "Real Estate Agents" },
  { src: "/tours%20travel.webp", label: "Tours & Travels" },
  { src: "/institutes.webp", label: "Education & Coaching" },
  { src: "/service%20industry.webp", label: "Handyman Services" },
  { src: "/pest%20control.webp", label: "Pest Control Businesses" },
];

const UNIT_TERMS = [
  "Client Acquisition Cost (CAC)",
  "Average Revenue Per Client (ARPC)",
  "Monthly Recurring Revenue (MRR)",
  "Annual Recurring Revenue (ARR)",
  "Gross Margin",
  "Client Retention",
  "Lifetime Value (LTV)",
  "Upsell Revenue",
];

// ---------------------------------------------------------------------------

function SectionHeading({ overline, title, subtitle, className = "" }) {
  return (
    <Reveal className={`max-w-2xl mx-auto text-center ${className}`}>
      {overline && <Overline>{overline}</Overline>}
      <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
        {title}
      </h2>
      {subtitle && <p className="mt-4 text-base md:text-lg text-[#475569] leading-relaxed">{subtitle}</p>}
    </Reveal>
  );
}

function Disclaimer({ children, className = "" }) {
  return <p className={`text-xs text-[#94A3B8] italic ${className}`}>{children}</p>;
}

function HeroSection() {
  return (
    <section className="relative pt-32 pb-20 overflow-hidden bg-white border-b border-[#E6E9F1]">
      <div className="mb-blob mb-blob-orange mb-anim-a w-[380px] h-[380px] -top-24 right-0" />
      <div className="mb-blob mb-blob-blue mb-anim-b w-[320px] h-[320px] top-10 -left-20" />
      <div className="absolute inset-0 mb-grid-lines opacity-60" />
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
            <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
            <span className="text-xs font-medium text-[#475569]">Agency Earning Potential & Resale Pricing</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
            Build Your Own Profitable <span className="mb-gradient-text">Digital & AI Agency</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
            Sell AI automation, digital business software and marketing solutions under your own brand with
            MagicBolt White-Label.
          </p>
          <p className="mt-3 text-sm font-medium text-[#94A3B8] max-w-xl mx-auto">
            Choose your services, set your client pricing and build recurring monthly or yearly revenue from your
            own agency.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild className="rounded-full h-12 px-7 font-semibold">
              <a href="#agency-calculator">
                Calculate My Agency Revenue <ArrowRight className="ml-1 w-4 h-4" />
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-7 font-semibold">
              <a href="#agency-tiers">View Resale Pricing</a>
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold text-[#0B1533]">
            {["Your Brand", "Your Pricing", "Your Client Revenue"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                <Check className="w-4 h-4 text-[#FF6B00]" strokeWidth={3} />
                {t}
              </span>
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <CurrencySelector />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TargetAudienceSection() {
  return (
    <section className="py-20 md:py-28 bg-white border-b border-[#E6E9F1]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="Who You'll Sell To"
          title="Your Targeted Customers, Near You"
          subtitle="Local businesses like these are already searching nearby - MagicBolt AI helps you find, sell to and automate growth for them."
        />

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
          {INDUSTRY_PHOTOS.map((industry, i) => (
            <Reveal key={industry.label} delay={i * 0.05}>
              <div className="group h-full overflow-hidden rounded-2xl border border-[#E6E9F1] bg-white mb-card-shadow transition-shadow duration-300 hover:mb-card-shadow-hover">
                <div className="aspect-square overflow-hidden bg-[#F4F6FB]">
                  <img
                    src={industry.src}
                    alt={industry.label}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="px-4 py-3.5 text-center">
                  <span className="text-sm font-bold text-[#0B1533]">{industry.label}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-10 text-center text-lg md:text-xl font-bold text-[#0B1533]">
          Sell Services to Every Local or Global Business
        </p>
        <Disclaimer className="mt-2 text-center">
          And many more local business categories beyond those shown here.
        </Disclaimer>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section className="py-20 md:py-24 bg-[#F4F6FB]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading overline="The Model" title="Turn MagicBolt Into Your Own Agency Business" />
        <div className="mt-14 grid gap-6 md:grid-cols-5">
          {STEPS.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.08} className="relative">
              <div className="h-full rounded-xl border border-[#E6E9F1] bg-white p-6 mb-card-shadow">
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#0B1533] text-white text-sm font-bold">
                  {i + 1}
                </div>
                <h3 className="mt-4 text-sm font-bold text-[#0B1533] leading-snug">{step.title}</h3>
                <p className="mt-2 text-xs text-[#475569] leading-relaxed">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatCanYouSellSection() {
  return (
    <section className="py-20 md:py-24 bg-white border-t border-[#E6E9F1]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading overline="Product Categories" title="What Can You Sell With MagicBolt?" />
        <div className="mt-12 grid gap-6 md:grid-cols-3 items-stretch">
          {CATEGORIES.map((cat, i) => (
            <Reveal key={cat.title} delay={i * 0.08}>
              <div className="h-full rounded-xl border border-[#E6E9F1] bg-white p-7 mb-card-shadow">
                <div
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg"
                  style={{ background: cat.tint }}
                >
                  <cat.icon className="w-5 h-5" style={{ color: cat.color }} />
                </div>
                <h3 className="mt-4 text-base font-bold text-[#0B1533]">{cat.title}</h3>
                <ul className="mt-4 space-y-2">
                  {cat.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#475569]">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: cat.color }} strokeWidth={3} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChannelBadge({ channel }) {
  const cfg = CHANNEL_ICONS[channel];
  return (
    <span
      className={`flex items-center justify-center w-7 h-7 rounded-full ${cfg.border ? "border border-[#E6E9F1]" : ""}`}
      style={{ background: cfg.bg }}
    >
      <cfg.Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
    </span>
  );
}

function TierPriceGrid({ prices, unit, money, selectedTier }) {
  return (
    <>
      <div className="mt-4 grid grid-cols-3 gap-2.5">
        {prices.map((price, ti) => {
          const active = ti === selectedTier;
          return (
            <div
              key={ti}
              className={`rounded-lg border p-3 text-center transition-colors ${
                active ? "border-[#FF6B00] bg-[#FFF3E9]" : "border-[#E6E9F1] bg-[#F4F6FB]"
              }`}
            >
              <div
                className={`text-[10px] font-bold uppercase tracking-wider ${
                  active ? "text-[#FF6B00]" : "text-[#94A3B8]"
                }`}
              >
                Tier {ti + 1}
              </div>
              <div
                className={`mt-1 text-base font-extrabold tracking-tight ${
                  active ? "text-[#FF6B00]" : "text-[#0B1533]"
                }`}
              >
                {money(price)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-center text-xs font-semibold text-[#94A3B8]">{unit}</div>
    </>
  );
}

function TiersSection() {
  const money = useMoney();
  const { currency } = useCurrency();
  const [selectedTier, setSelectedTier] = useState(1);
  const [customersPerYear, setCustomersPerYear] = useState(20);
  const [channelsPerCustomer, setChannelsPerCustomer] = useState(3);
  const [softwarePerCustomer, setSoftwarePerCustomer] = useState("All");

  // Channel revenue: 3 channels sells at the bundled combined-price, not
  // 3x the single-channel rate. Software revenue averages the two software
  // suite cards (QR is priced /year, so it's converted to a monthly rate).
  const channelRevenue =
    channelsPerCustomer === 3
      ? ALL_CHANNELS_COMBINED.prices[selectedTier]
      : channelsPerCustomer * SINGLE_CHANNEL.prices[selectedTier];
  const qrMonthlyEquivalent = QR_SUITE_PRICES[selectedTier] / 12;
  const businessMonthly = PRODUCT_SUITE_CARDS[1].prices[selectedTier];
  const avgSoftwarePrice = (qrMonthlyEquivalent + businessMonthly) / 2;
  const softwareCount = softwarePerCustomer === "All" ? 2 : softwarePerCustomer;
  const softwareRevenue = softwareCount * avgSoftwarePrice;

  const perCustomerMonthly = channelRevenue + softwareRevenue;
  const estMonthly = customersPerYear * perCustomerMonthly;
  const estAnnual = estMonthly * 12;
  const range = (n) => `${formatCurrencyCompact(n * 0.85, currency)} – ${formatCurrencyCompact(n * 1.15, currency)}`;

  return (
    <section id="agency-tiers" className="py-20 md:py-24 bg-[#F4F6FB] border-t border-[#E6E9F1] scroll-mt-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="Example Client Packages"
          title="Create Your Own Client Packages"
          subtitle="These are example resale packages. Your agency can customize pricing based on industry, market, scope and service level."
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-3 items-stretch">
          {PACKAGE_CATEGORIES.map((cat, i) => (
            <Reveal key={cat.title} delay={i * 0.1} className="flex flex-col">
              <div className="flex flex-1 flex-col rounded-xl border border-[#E6E9F1] bg-white p-7 mb-card-shadow">
                <div className="flex items-center gap-1.5">
                  {cat.channels.map((c) => (
                    <ChannelBadge key={c} channel={c} />
                  ))}
                </div>
                <h3 className="mt-4 text-lg font-bold text-[#0B1533]">{cat.title}</h3>
                <p className="mt-2 text-sm text-[#475569] min-h-[60px]">{cat.subtitle}</p>

                <TierPriceGrid prices={cat.prices} unit={cat.unit} money={money} selectedTier={selectedTier} />
              </div>
            </Reveal>
          ))}

          {PRODUCT_SUITE_CARDS.map((cat, i) => (
            <Reveal key={cat.key} delay={(PACKAGE_CATEGORIES.length + i) * 0.1} className="flex flex-col">
              <div className="flex flex-1 flex-col rounded-xl border border-[#E6E9F1] bg-white p-7 mb-card-shadow">
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#FF6B00]/10">
                  <cat.icon className="w-4 h-4 text-[#FF6B00]" />
                </span>
                <h3 className="mt-4 text-lg font-bold text-[#0B1533]">{cat.title}</h3>
                <p className="mt-2 text-sm text-[#475569] min-h-[60px]">{cat.subtitle}</p>

                <TierPriceGrid prices={cat.prices} unit={cat.unit} money={money} selectedTier={selectedTier} />

                <a
                  href={cat.anchor}
                  className="mt-4 text-center text-sm font-semibold text-[#FF6B00] hover:underline"
                >
                  View Full Pricing ↓
                </a>
              </div>
            </Reveal>
          ))}

          <Reveal
            id="estimate-earnings"
            delay={(PACKAGE_CATEGORIES.length + PRODUCT_SUITE_CARDS.length) * 0.1}
            className="flex flex-col scroll-mt-24"
          >
            <div className="flex flex-1 flex-col rounded-xl border border-[#FF6B00] bg-gradient-to-b from-[#FFF3E9] to-white p-7 mb-card-shadow-hover">
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#FF6B00]/10">
                <Calculator className="w-4 h-4 text-[#FF6B00]" />
              </span>
              <h3 className="mt-4 text-lg font-bold text-[#0B1533]">Estimate Your Earnings</h3>
              <p className="mt-2 text-sm text-[#475569]">
                Pick your tier, customers, channels and software mix - every card above highlights that tier's
                pricing.
              </p>

              <div className="mt-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">
                  Which Tier Are You On?
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {PACKAGE_TIERS.map((t, i) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setSelectedTier(i)}
                      className={`rounded-lg border px-2 py-2 text-xs font-bold transition-colors ${
                        selectedTier === i
                          ? "border-[#FF6B00] bg-[#FF6B00] text-white"
                          : "border-[#E6E9F1] bg-white text-[#475569]"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">
                  Customers You Can Achieve in a Year
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[20, 50, 100].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setCustomersPerYear(n)}
                      className={`rounded-lg border px-2 py-2 text-xs font-bold transition-colors ${
                        customersPerYear === n
                          ? "border-[#FF6B00] bg-[#FF6B00] text-white"
                          : "border-[#E6E9F1] bg-white text-[#475569]"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">
                  Channels Per Customer
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setChannelsPerCustomer(n)}
                      className={`rounded-lg border px-2 py-2 text-xs font-bold transition-colors ${
                        channelsPerCustomer === n
                          ? "border-[#FF6B00] bg-[#FF6B00] text-white"
                          : "border-[#E6E9F1] bg-white text-[#475569]"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">
                  Software Suites Per Customer
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[1, 2, "All"].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setSoftwarePerCustomer(n)}
                      className={`rounded-lg border px-2 py-2 text-xs font-bold transition-colors ${
                        softwarePerCustomer === n
                          ? "border-[#FF6B00] bg-[#FF6B00] text-white"
                          : "border-[#E6E9F1] bg-white text-[#475569]"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex-1 grid grid-rows-2 gap-2.5">
                <div className="rounded-lg bg-white border border-[#E6E9F1] p-3 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">
                    Approx. Monthly
                  </div>
                  <div className="mt-1 text-lg font-extrabold text-[#0B1533] tracking-tight">
                    {range(estMonthly)}
                  </div>
                </div>
                <div className="rounded-lg bg-white border border-[#E6E9F1] p-3 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">
                    Approx. Annual
                  </div>
                  <div className="mt-1 text-lg font-extrabold text-[#FF6B00] tracking-tight">{range(estAnnual)}</div>
                </div>
              </div>

              <Disclaimer className="mt-4">
                Based on {customersPerYear} clients at {PACKAGE_TIERS[selectedTier].label}, each buying{" "}
                {channelsPerCustomer} channel{channelsPerCustomer > 1 ? "s" : ""} and{" "}
                {softwarePerCustomer === "All" ? "all" : softwarePerCustomer} software suite
                {softwarePerCustomer !== 1 ? "s" : ""} - illustrative only, not guaranteed.
              </Disclaimer>
            </div>
          </Reveal>
        </div>

        <Disclaimer className="mt-8 text-center">
          Suggested Agency Retail Price · Mix and Match Any Combination · Your Agency Sets the Final Price.
        </Disclaimer>
      </div>
    </section>
  );
}

function QrSuiteSection() {
  const money = useMoney();
  return (
    <section id="qr-suite" className="py-20 md:py-24 bg-white border-t border-[#E6E9F1] scroll-mt-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="QR Software Suite"
          title="Sell MagicQR, Visiting Cards, Menus & Bio Links"
          subtitle="Priced per product, per year - package these alongside any automation tier above."
        />

        <Reveal delay={0.1} className="mt-10 overflow-x-auto rounded-xl border border-[#E6E9F1] bg-white mb-card-shadow">
          <table className="w-full min-w-[520px] text-sm border-collapse">
            <thead>
              <tr className="border-b border-[#E6E9F1] bg-[#F9FAFC]">
                <th className="text-left px-6 py-4 font-bold text-[#0B1533]">Product</th>
                <th className="px-4 py-4 font-bold text-[#0B1533] text-center whitespace-nowrap">Tier 1 / yr</th>
                <th className="px-4 py-4 font-bold text-[#0B1533] text-center whitespace-nowrap">Tier 2 / yr</th>
                <th className="px-6 py-4 font-bold text-[#0B1533] text-center whitespace-nowrap">Tier 3 / yr</th>
              </tr>
            </thead>
            <tbody>
              {QR_SUITE_PRODUCTS.map((product) => (
                <tr key={product} className="border-b border-[#E6E9F1] last:border-0 hover:bg-[#F9FAFC]">
                  <td className="px-6 py-3.5 text-[#475569]">{product}</td>
                  {QR_SUITE_PRICES.map((price, i) => (
                    <td key={i} className="px-4 py-3.5 text-center font-semibold text-[#0B1533] whitespace-nowrap">
                      {money(price)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <Disclaimer className="mt-4 text-center">Suggested Retail Pricing - Set Your Own Agency Prices.</Disclaimer>
      </div>
    </section>
  );
}

function BusinessSoftwareSection() {
  const money = useMoney();
  return (
    <section id="business-software" className="py-20 md:py-24 bg-[#F4F6FB] border-t border-[#E6E9F1] scroll-mt-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="Instant Website"
          title="Sell Instant Websites"
          subtitle="Priced monthly or yearly - stack it onto any package above."
        />

        <Reveal delay={0.1} className="mt-10 overflow-x-auto rounded-xl border border-[#E6E9F1] bg-white mb-card-shadow">
          <table className="w-full min-w-[520px] text-sm border-collapse">
            <thead>
              <tr className="border-b border-[#E6E9F1] bg-[#F9FAFC]">
                <th className="text-left px-6 py-4 font-bold text-[#0B1533]">Product</th>
                <th className="px-4 py-4 font-bold text-[#0B1533] text-center whitespace-nowrap">Monthly</th>
                <th className="px-6 py-4 font-bold text-[#0B1533] text-center whitespace-nowrap">Yearly</th>
              </tr>
            </thead>
            <tbody>
              {BUSINESS_SOFTWARE.map((row) => (
                <tr key={row.product} className="border-b border-[#E6E9F1] last:border-0 hover:bg-[#F9FAFC]">
                  <td className="px-6 py-3.5 text-[#475569]">
                    <div className="flex items-center gap-2.5">
                      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#FF6B00]/10 shrink-0">
                        <row.icon className="w-3.5 h-3.5 text-[#FF6B00]" />
                      </span>
                      {row.product}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-center font-semibold text-[#0B1533] whitespace-nowrap">
                    {money(row.monthly)}
                  </td>
                  <td className="px-6 py-3.5 text-center font-semibold text-[#0B1533] whitespace-nowrap">
                    {money(row.yearly)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <Disclaimer className="mt-4 text-center">Suggested Retail Pricing - Set Your Own Agency Prices.</Disclaimer>
      </div>
    </section>
  );
}

function CalculatorSection() {
  const { currency } = useCurrency();
  const growthTierMonthly = PACKAGE_TIERS[1].monthly;

  const [clients, setClients] = useState(25);
  const [avgMonthly, setAvgMonthly] = useState(() => convertFromINR(growthTierMonthly, currency));
  const [setupFee, setSetupFee] = useState(0);

  // Currency figures the user has typed only make sense in the currency
  // they were typed in - reset to sensible converted defaults on switch
  // rather than silently reinterpreting stale numbers in a new currency.
  useEffect(() => {
    setAvgMonthly(convertFromINR(growthTierMonthly, currency));
    setSetupFee(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency.code]);

  const fmt = (n) => `${currency.symbol}${Math.round(n).toLocaleString("en-US")}`;

  const { mrr, arr, oneTime } = useMemo(() => {
    const c = Number(clients) || 0;
    const m = Number(avgMonthly) || 0;
    const s = Number(setupFee) || 0;
    return { mrr: c * m, arr: c * m * 12, oneTime: c * s };
  }, [clients, avgMonthly, setupFee]);

  return (
    <section id="agency-calculator" className="py-20 md:py-24 bg-white border-t border-[#E6E9F1] scroll-mt-24">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading overline="Interactive" title="See Your Agency Revenue Potential" />

        <Reveal delay={0.1} className="mt-12 grid gap-8 lg:grid-cols-2 items-start">
          <div className="rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] p-7">
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#FF6B00]" />
              <h3 className="text-sm font-bold text-[#0B1533]">Agency Earning Calculator</h3>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between text-sm font-semibold text-[#0B1533]">
                <span>Number of Clients</span>
                <span>{clients}</span>
              </div>
              <input
                type="range"
                min="1"
                max="150"
                value={clients}
                onChange={(e) => setClients(Number(e.target.value))}
                className="mt-2 w-full accent-[#FF6B00]"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {CALCULATOR_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setClients(p)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      clients === p
                        ? "bg-[#FF6B00] text-white"
                        : "bg-white border border-[#E6E9F1] text-[#475569] hover:border-[#FF6B00]/40"
                    }`}
                  >
                    {p} Clients
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm font-semibold text-[#0B1533]">
                Average Monthly Package ({currency.symbol})
              </label>
              <input
                type="number"
                min="0"
                value={avgMonthly}
                onChange={(e) => setAvgMonthly(e.target.value)}
                className="mt-2 w-full rounded-lg border border-[#E6E9F1] bg-white px-4 py-2.5 text-sm font-semibold text-[#0B1533] outline-none focus:border-[#FF6B00]"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {PACKAGE_TIERS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setAvgMonthly(convertFromINR(t.monthly, currency))}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      Number(avgMonthly) === convertFromINR(t.monthly, currency)
                        ? "bg-[#FF6B00] text-white"
                        : "bg-white border border-[#E6E9F1] text-[#475569] hover:border-[#FF6B00]/40"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm font-semibold text-[#0B1533]">
                Optional One-Time Setup Fee Per Client ({currency.symbol})
              </label>
              <input
                type="number"
                min="0"
                value={setupFee}
                onChange={(e) => setSetupFee(e.target.value)}
                className="mt-2 w-full rounded-lg border border-[#E6E9F1] bg-white px-4 py-2.5 text-sm font-semibold text-[#0B1533] outline-none focus:border-[#FF6B00]"
              />
            </div>
          </div>

          <div className="rounded-xl border border-[#E6E9F1] bg-white p-7 mb-card-shadow">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">
              Illustrative Gross Revenue
            </div>

            <div className="mt-5 grid gap-4">
              <div className="rounded-lg bg-[#FFF3E9] p-5">
                <div className="text-xs font-bold uppercase tracking-wide text-[#FF6B00]">
                  Monthly Recurring Revenue (MRR)
                </div>
                <AnimatedNumber
                  value={mrr}
                  format={fmt}
                  className="mt-1 block text-3xl font-extrabold text-[#0B1533] tracking-tight"
                />
              </div>
              <div className="rounded-lg bg-[#F4F6FB] p-5">
                <div className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">
                  Annual Recurring Revenue (ARR)
                </div>
                <AnimatedNumber
                  value={arr}
                  format={fmt}
                  className="mt-1 block text-3xl font-extrabold text-[#0B1533] tracking-tight"
                />
              </div>
              {oneTime > 0 && (
                <div className="rounded-lg bg-[#F4F6FB] p-5">
                  <div className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">
                    Illustrative One-Time Revenue
                  </div>
                  <AnimatedNumber
                    value={oneTime}
                    format={fmt}
                    className="mt-1 block text-3xl font-extrabold text-[#0B1533] tracking-tight"
                  />
                </div>
              )}
            </div>

            <Disclaimer className="mt-5">
              Revenue is not profit. Actual profit depends on your MagicBolt plan, third-party costs, sales expenses,
              team costs, client acquisition costs, taxes and service delivery costs.
            </Disclaimer>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PersonalizedEstimateSection() {
  const { currency } = useCurrency();
  const [name, setName] = useState("");
  const [tierIndex, setTierIndex] = useState(1);
  const [customersPerMonth, setCustomersPerMonth] = useState(10);
  const [channelsSold, setChannelsSold] = useState(5);
  const [combinedSold, setCombinedSold] = useState(2);
  const [result, setResult] = useState(null);

  const fmt = (n) => `${currency.symbol}${Math.round(n).toLocaleString("en-US")}`;

  const handleCalculate = () => {
    const singlePrice = convertFromINR(SINGLE_CHANNEL.prices[tierIndex], currency);
    const combinedPrice = convertFromINR(ALL_CHANNELS_COMBINED.prices[tierIndex], currency);
    const baseMonthly = (Number(channelsSold) || 0) * singlePrice + (Number(combinedSold) || 0) * combinedPrice;
    const monthlyLow = baseMonthly * 0.85;
    const monthlyHigh = baseMonthly * 1.15;
    setResult({ monthlyLow, monthlyHigh, yearlyLow: monthlyLow * 12, yearlyHigh: monthlyHigh * 12 });
  };

  return (
    <section className="py-20 md:py-24 bg-[#0B1533] border-t border-[#E6E9F1]">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          <Overline className="!text-[#FF8533]">Personalized Estimate</Overline>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tighter leading-none text-white">
            Get Your Own Earnings Range
          </h2>
          <p className="mt-4 text-base md:text-lg text-white/70 leading-relaxed max-w-xl mx-auto">
            Enter your tier, and how many channel sales and combined packages you expect to close each month - we'll
            give you an approximate monthly and yearly range.
          </p>

          <Dialog onOpenChange={(open) => !open && setResult(null)}>
            <DialogTrigger asChild>
              <Button className="mt-8 rounded-full h-12 px-7 font-semibold">
                Calculate My Earnings Range <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[#0B1533]">Your Earnings Estimate</DialogTitle>
                <DialogDescription>
                  Illustrative only - your actual revenue depends on your clients, pricing, sales and delivery.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="text-left">
                  <label className="text-sm font-semibold text-[#0B1533]">Your Name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Rahul"
                    className="mt-1.5 w-full rounded-lg border border-[#E6E9F1] bg-white px-3 py-2 text-sm text-[#0B1533] outline-none focus:border-[#FF6B00]"
                  />
                </div>

                <div className="text-left">
                  <label className="text-sm font-semibold text-[#0B1533]">Your Tier</label>
                  <div className="mt-1.5 grid grid-cols-3 gap-2">
                    {PACKAGE_TIERS.map((t, i) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setTierIndex(i)}
                        className={`rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${
                          tierIndex === i
                            ? "border-[#FF6B00] bg-[#FFF3E9] text-[#FF6B00]"
                            : "border-[#E6E9F1] text-[#475569]"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="text-left">
                  <label className="text-sm font-semibold text-[#0B1533]">New Customers Per Month</label>
                  <input
                    type="number"
                    min="0"
                    value={customersPerMonth}
                    onChange={(e) => setCustomersPerMonth(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-[#E6E9F1] bg-white px-3 py-2 text-sm font-semibold text-[#0B1533] outline-none focus:border-[#FF6B00]"
                  />
                </div>

                <div className="text-left">
                  <label className="text-sm font-semibold text-[#0B1533]">
                    Channels You'll Sell Separately / Month
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={channelsSold}
                    onChange={(e) => setChannelsSold(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-[#E6E9F1] bg-white px-3 py-2 text-sm font-semibold text-[#0B1533] outline-none focus:border-[#FF6B00]"
                  />
                </div>

                <div className="text-left">
                  <label className="text-sm font-semibold text-[#0B1533]">
                    All-Channels-Combined Packages / Month
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={combinedSold}
                    onChange={(e) => setCombinedSold(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-[#E6E9F1] bg-white px-3 py-2 text-sm font-semibold text-[#0B1533] outline-none focus:border-[#FF6B00]"
                  />
                </div>
              </div>

              <Button onClick={handleCalculate} className="w-full rounded-full h-11 font-semibold">
                Calculate My Range
              </Button>

              {result && (
                <div className="rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] p-5 text-left">
                  <p className="text-sm font-semibold text-[#0B1533]">
                    {name ? `${name}, here's` : "Here's"} your illustrative estimate at {PACKAGE_TIERS[tierIndex].label}
                    {Number(customersPerMonth) > 0 ? ` for ~${customersPerMonth} customers/month` : ""}:
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-white p-3 text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">
                        Monthly Range
                      </div>
                      <div className="mt-1 text-sm font-extrabold text-[#0B1533]">
                        {fmt(result.monthlyLow)} – {fmt(result.monthlyHigh)}
                      </div>
                    </div>
                    <div className="rounded-lg bg-white p-3 text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">
                        Yearly Range
                      </div>
                      <div className="mt-1 text-sm font-extrabold text-[#FF6B00]">
                        {fmt(result.yearlyLow)} – {fmt(result.yearlyHigh)}
                      </div>
                    </div>
                  </div>
                  <Disclaimer className="mt-3">
                    Illustrative Gross Revenue - not profit, and not guaranteed.
                  </Disclaimer>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </Reveal>
      </div>
    </section>
  );
}

function BundlesSection() {
  return (
    <section className="py-20 md:py-24 bg-white border-t border-[#E6E9F1]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="Higher-Value Packages"
          title="Don't Sell Software Alone. Sell Business Solutions."
          subtitle="Software + Automation + Setup + Management + Strategy - combined into one package your clients understand."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {BUNDLES.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.06}>
              <div className="h-full rounded-xl border border-[#E6E9F1] bg-white p-6 mb-card-shadow">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#FF6B00]/10">
                  <b.icon className="w-5 h-5 text-[#FF6B00]" />
                </div>
                <h3 className="mt-4 text-base font-bold text-[#0B1533]">{b.name}</h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {b.items.map((item) => (
                    <span key={item} className="rounded-full bg-[#F4F6FB] px-2.5 py-1 text-xs font-medium text-[#475569]">
                      {item}
                    </span>
                  ))}
                </div>
                <div className="mt-4 text-sm font-bold text-[#0B1533]">Suggested Client Price: {b.price}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function OwnershipSection() {
  return (
    <section className="py-20 md:py-24 bg-white border-t border-[#E6E9F1]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="What You Actually Own"
          title="Your Brand. Your Clients. Your Pricing."
          subtitle="MagicBolt provides the technology infrastructure. You build the business around it."
        />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          {OWNERSHIP.map((item, i) => (
            <Reveal key={item} delay={i * 0.05}>
              <div className="flex items-center gap-3 rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] p-5">
                <ShieldCheck className="w-5 h-5 text-[#FF6B00] shrink-0" />
                <span className="text-sm font-bold text-[#0B1533]">{item}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function RevenueStreamsSection() {
  return (
    <section className="py-20 md:py-24 bg-white border-t border-[#E6E9F1]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="ARPC"
          title="Your Revenue Doesn't Have to Come From Software Alone"
          subtitle="The same client can purchase multiple services, increasing Average Revenue Per Client (ARPC)."
        />
        <div className="mt-10 grid gap-3 sm:grid-cols-2">
          {REVENUE_STREAMS.map((item, i) => (
            <Reveal key={item} delay={i * 0.03}>
              <div className="flex items-center gap-2.5 rounded-lg border border-[#E6E9F1] bg-[#F4F6FB] px-4 py-3">
                <Wallet className="w-4 h-4 text-[#FF6B00] shrink-0" />
                <span className="text-sm font-medium text-[#0B1533]">{item}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function UnitEconomicsSection() {
  return (
    <section className="py-20 md:py-24 bg-[#F4F6FB] border-t border-[#E6E9F1]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading overline="Know Your Numbers" title="Understand Your Agency Economics" />

        <Reveal delay={0.1} className="mt-10 flex flex-wrap justify-center gap-2.5">
          {UNIT_TERMS.map((term) => (
            <span
              key={term}
              className="rounded-full border border-[#E6E9F1] bg-white px-4 py-2 text-sm font-semibold text-[#0B1533]"
            >
              {term}
            </span>
          ))}
        </Reveal>

        <Reveal delay={0.15} className="mt-10 grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-[#E6E9F1] bg-white p-5 text-center">
            <div className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">MRR</div>
            <div className="mt-2 text-sm font-semibold text-[#0B1533]">
              Active Clients × Average Monthly Revenue Per Client
            </div>
          </div>
          <div className="rounded-xl border border-[#E6E9F1] bg-white p-5 text-center">
            <div className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">ARR</div>
            <div className="mt-2 text-sm font-semibold text-[#0B1533]">MRR × 12</div>
          </div>
          <div className="rounded-xl border border-[#E6E9F1] bg-white p-5 text-center">
            <div className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Potential Gross Margin</div>
            <div className="mt-2 text-sm font-semibold text-[#0B1533]">
              Client Revenue − Platform Costs − Delivery Costs − Third-Party Costs
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCtaSection() {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-[#0B1533]">
      <div className="mb-blob mb-blob-orange mb-anim-a w-[420px] h-[420px] -top-32 right-0 opacity-40" />
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter leading-tight text-white">
            Your Agency Doesn't Need to Build the Technology.
          </h2>
          <p className="mt-4 text-base md:text-lg text-white/70 leading-relaxed">
            MagicBolt gives you the platform. You bring the brand, clients and growth.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild className="rounded-full h-12 px-7 font-semibold">
              <a href={SIGNUP_URL}>
                Start Your White-Label Agency <ArrowRight className="ml-1 w-4 h-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full h-12 px-7 font-semibold bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
            >
              <a href="#agency-calculator">Calculate My Revenue Potential</a>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// Sits to the left of the global WhatsApp button (48px at right-4 on
// mobile, 56px at right-5 from sm), vertically centred on it.
function FloatingEstimateButton() {
  return (
    <a
      href="#estimate-earnings"
      className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[4.5rem] z-40 inline-flex sm:bottom-[calc(1.25rem+env(safe-area-inset-bottom))] sm:right-[5.5rem] items-center gap-2 rounded-full bg-[#FF6B00] px-5 py-3.5 text-sm font-bold text-white shadow-[0_10px_30px_rgba(255,107,0,0.4)] transition-transform hover:-translate-y-0.5 hover:bg-[#FF8533]"
    >
      <Calculator className="w-4 h-4" />
      <span className="hidden sm:inline">Estimate My Earnings</span>
      <span className="sm:hidden">Estimate</span>
    </a>
  );
}

function HowAgenciesEarnPageContent() {
  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="how-agencies-earn-page">
      <SEO
        title="Agency Earning Potential & Resale Pricing | MagicBolt AI"
        description="See how agencies price, package and resell MagicBolt AI's automation and software - example client tiers, a revenue calculator and growth scenarios."
        path="/how-agencies-earn"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "How Agencies Earn", path: "/how-agencies-earn" },
        ]}
      />
      <Navbar />

      <HeroSection />
      <TargetAudienceSection />
      <HowItWorksSection />
      <WhatCanYouSellSection />
      <TiersSection />
      <QrSuiteSection />
      <BusinessSoftwareSection />
      <CalculatorSection />
      <PersonalizedEstimateSection />
      <BundlesSection />
      <OwnershipSection />
      <RevenueStreamsSection />
      <UnitEconomicsSection />
      <PricingAddons />
      <FaqSection title="Frequently asked questions about earning with MagicBolt AI" items={HOW_AGENCIES_EARN_FAQS} />

      <CredibilityStrip />
      <FinalCtaSection />
      <Footer showCta={false} />

      <FloatingEstimateButton />
    </div>
  );
}

export default function HowAgenciesEarnPage() {
  return (
    <CurrencyProvider>
      <HowAgenciesEarnPageContent />
    </CurrencyProvider>
  );
}
